import React, { useMemo } from 'react';
import { CANALES_DISPONIBLES, tiposConversionList } from '../../../data/listasDesplegables';
import { PercentInput } from '../../../utils/FormInputs';

/**
 * SectionComercialArt - Sección para la selección de equipo comercial, 
 * contador interviniente, tipo de conversión y porcentajes de comisiones del canal.
 * Actualizado: Todos los desplegables ordenados alfabéticamente de forma memorizada.
 */
const SectionComercialArt = ({ state, actions }) => {
    const { formData, dbMasters, dbSubcanales } = state;
    const { 
        handleChange, 
        handlePercentChange, 
        handleCanalTypeChange,
        handleCanalIdChange, 
        handleSubcanalIdChange,
        setFormData 
    } = actions;

    // --- ORDENAMIENTO ALFABÉTICO EN MEMORIA ---
    const sortedCanalesTipos = useMemo(() => {
        return [...(CANALES_DISPONIBLES || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    const sortedCanalesNombres = useMemo(() => {
        return [...(dbMasters?.canales || [])]
            .filter(c => c.canal === formData.canal)
            .sort((a, b) => (a.nombreDisplay || a.nombre || '').localeCompare(b.nombreDisplay || b.nombre || ''));
    }, [dbMasters?.canales, formData.canal]);

    const sortedSubcanales = useMemo(() => {
        return [...(dbSubcanales || [])]
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbSubcanales]);

    const sortedContadores = useMemo(() => {
        return [...(dbMasters?.contadores || [])]
            .sort((a, b) => {
                const nombreCompletoA = `${a.nombre || ''} ${a.apellido || ''}`.trim();
                const nombreCompletoB = `${b.nombre || ''} ${b.apellido || ''}`.trim();
                return nombreCompletoA.localeCompare(nombreCompletoB);
            });
    }, [dbMasters?.contadores]);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])]
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbMasters?.colaboradores]);

    const sortedTiposConversion = useMemo(() => {
        return [...(tiposConversionList || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    return (
        <fieldset className="border border-blue-200 rounded-xl p-6 bg-blue-50/20 text-left shadow-sm animate-fade-in transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-blue-800 tracking-tight">Equipo Comercial & Comisiones</legend>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                
                {/* 1. TIPO DE CANAL */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Canal *</label>
                    <select 
                        name="canal" 
                        value={formData.canal || ''} 
                        onChange={(e) => handleCanalTypeChange(e.target.value)}
                        className="input-round font-bold border-slate-200 w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all" 
                        required
                    >
                        <option value="">Seleccione Canal...</option>
                        {sortedCanalesTipos.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>

                {/* 2. NOMBRE DEL CANAL */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Nombre del Canal *</label>
                    <div className="flex gap-2">
                        <select 
                            value={formData.canalNombreId || ''} 
                            onChange={(e) => handleCanalIdChange(e.target.value)} 
                            className="input-round border-slate-200 flex-grow rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all font-bold" 
                            disabled={!formData.canal} 
                            required
                        >
                            <option value="">Seleccione...</option>
                            {sortedCanalesNombres.map(c => (
                                <option key={c.id} value={c.id}>{c.nombreDisplay || c.nombre}</option>
                            ))}
                        </select>
                        <div className="w-24 shrink-0">
                            <PercentInput 
                                name="comisionOrganizador" 
                                value={formData.comisionOrganizador} 
                                onChange={handlePercentChange} 
                                placeholder="0,00"
                                focusColor="focus-within:border-orange-300 focus-within:ring-1 focus-within:ring-orange-100"
                                textColor="text-orange-700 font-bold"
                            />
                        </div>
                    </div>
                </div>

                {/* 3. SUBCANAL ASOCIADO */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Subcanal Asociado</label>
                    <div className="flex gap-2">
                        <select 
                            value={formData.subcanalNombreId || ''} 
                            onChange={(e) => handleSubcanalIdChange(e.target.value)} 
                            className="input-round border-slate-200 flex-grow rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all font-bold"
                            disabled={sortedSubcanales.length === 0}
                        >
                            <option value="">(Ninguno)</option>
                            {sortedSubcanales.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>
                        <div className="w-24 shrink-0">
                            <PercentInput 
                                name="comisionAsociado" 
                                value={formData.comisionAsociado} 
                                onChange={handlePercentChange} 
                                placeholder="0,00"
                                focusColor="focus-within:border-emerald-300 focus-within:ring-1 focus-within:ring-emerald-100"
                                textColor="text-emerald-700 font-bold"
                                disabled={!formData.subcanalNombreId}
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* INTERVENCIÓN DEL CONTADOR & COLABORADORES */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                
                {/* INTERVENCION CONTADOR */}
                <div className="flex flex-col">
                    <label className="label-form text-slate-500 font-bold mb-1 block">¿Interviene Contador?</label>
                    <div className="flex gap-2 h-[46px]">
                        <label className={`flex items-center justify-center cursor-pointer px-4 rounded-xl border transition-all shrink-0 ${
                            formData.tieneContador ? 'bg-blue-600 border-blue-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}>
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                                formData.tieneContador ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-300'
                            }`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <input 
                                type="checkbox" 
                                name="tieneContador" 
                                checked={formData.tieneContador || false} 
                                onChange={handleChange} 
                                className="sr-only" 
                            />
                        </label>
                        <div className="flex-grow">
                            {formData.tieneContador ? (
                                <select 
                                    name="contadorId" 
                                    value={formData.contadorId || ''} 
                                    onChange={(e) => {
                                        const c = sortedContadores.find(x => x.id === e.target.value);
                                        setFormData(p => ({
                                            ...p, 
                                            contadorId: e.target.value, 
                                            contadorNombre: c ? `${c.nombre} ${c.apellido || ''}`.trim() : ''
                                        }));
                                    }} 
                                    className="input-round border-blue-300 bg-white font-bold animate-fade-in text-sm uppercase w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all"
                                >
                                    <option value="">Seleccionar Contador...</option>
                                    {sortedContadores.map(c => (
                                        <option key={c.id} value={c.id}>{c.nombre} {c.apellido || ''}</option>
                                    ))}
                                </select>
                            ) : (
                                <div className="input-round border-slate-100 bg-slate-50/50 flex items-center px-4 text-slate-400 text-xs italic w-full rounded-xl h-[46px] border">
                                    No aplica intervención
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* VENDEDOR */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Vendedor</label>
                    <select 
                        name="vendedor" 
                        value={formData.vendedor || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-blue-400 font-bold"
                    >
                        <option value="">Seleccione...</option>
                        {sortedColaboradores.map(c => (
                            <option key={c.id} value={c.nombre}>{c.nombre}</option>
                        ))}
                    </select>
                </div>

                {/* SUSCRIPTOR */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Suscriptor</label>
                    <select 
                        name="suscriptor" 
                        value={formData.suscriptor || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-blue-400 font-bold"
                    >
                        <option value="">Seleccione...</option>
                        {sortedColaboradores.map(c => (
                            <option key={c.id} value={c.nombre}>{c.nombre}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* TIPO DE CONVERSIÓN */}
            <div className="mt-8 pt-6 border-t border-blue-100">
                <div className="max-w-xs">
                    <label className="label-form text-slate-500 font-bold mb-1 block">Tipo de Conversión</label>
                    <select 
                        name="tipoConversion" 
                        value={formData.tipoConversion || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 focus:border-blue-400 font-bold w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all"
                    >
                        <option value="">Seleccione...</option>
                        {sortedTiposConversion.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>
            </div>
        </fieldset>
    );
};

export default SectionComercialArt;